import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { treatments } from "@/data/treatments";
import Reveal from "@/components/Reveal";

const Contact = () => {
  const { toast } = useToast();
  const [consent, setConsent] = useState(false);

  // La mappa Google (iframe di terze parti) viene caricata solo quando la
  // sezione entra nel viewport: niente cookie/JS di Google al primo caricamento.
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapVisible, setMapVisible] = useState(false);

  useEffect(() => {
    if (mapVisible) return;
    const el = mapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setMapVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMapVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mapVisible]);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    treatment: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!consent) {
      toast({
        title: "Consenso richiesto",
        description: "Per inviare la richiesta devi accettare la Privacy Policy.",
        variant: "destructive",
      });
      return;
    }

    const message = `Ciao! Sono ${formData.name}

Telefono: ${formData.phone}
Trattamento: ${formData.treatment || "Non specificato"}

Messaggio:
${formData.message}`;

    window.open(
      `https://wa.me/393461533000?text=${encodeURIComponent(message)}`,
      "_blank",
    );

    toast({
      title: "Richiesta inviata!",
      description: "Ti risponderò al più presto su WhatsApp.",
    });
    setFormData({ name: "", phone: "", treatment: "", message: "" });
    setConsent(false);
  };

  return (
    <section id="contatti" className="py-24 bg-gradient-to-b from-background to-secondary/40">
      <div className="container mx-auto px-4">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow">Contatti</p>
          <h2 className="mt-3 font-serif text-4xl md:text-6xl text-primary">
            Prenota il tuo momento
          </h2>
          <p className="mt-5 text-lg text-foreground/80">
            Ricevo solo su appuntamento. Scrivimi: ti risponderò personalmente.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <Reveal className="flex flex-col gap-6">
            <div className="bg-card p-8 rounded-3xl shadow-lg border border-border">
              <h3 className="font-serif text-2xl text-primary mb-6">Informazioni</h3>
              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
                    <MapPin className="text-accent w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-foreground font-medium">Via Roma, 334</p>
                    <p className="text-muted-foreground">35030 Montemerlo di Cervarese Santa Croce (PD)</p>
                    <a
                      href="https://maps.app.goo.gl/qixtEbsHeB6ssbyP7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-accent hover:underline"
                    >
                      Apri su Google Maps →
                    </a>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
                    <Phone className="text-accent w-5 h-5" />
                  </span>
                  <div>
                    <a
                      href="https://wa.me/393461533000"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-accent transition-colors font-medium"
                    >
                      +39 346 153 3000
                    </a>
                    <p className="text-sm text-muted-foreground">tramite messaggio WhatsApp</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
                    <Clock className="text-accent w-5 h-5" />
                  </span>
                  <div className="text-foreground">
                    <p className="font-medium">Solo su appuntamento</p>
                    <p className="text-muted-foreground text-sm">Lun–Ven 8:00–18:00 · Sab 8:00–16:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div
              ref={mapRef}
              className="relative rounded-3xl overflow-hidden shadow-lg border border-border flex-1 min-h-[280px] bg-secondary/40"
            >
              {mapVisible ? (
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d24168.328747565138!2d11.6658608!3d45.3841829!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477f27538e120439%3A0x9e4c18f33864c23b!2sORIGINI!5e0!3m2!1sit!2sit!4v1788606828624!5m2!1sit!2sit"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: 280 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="Mappa ORIGINI Studio Olistico - Via Roma 334, Montemerlo di Cervarese Santa Croce"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <MapPin className="w-6 h-6 text-accent" />
                  <span className="text-sm">Caricamento mappa…</span>
                </div>
              )}
              <a
                href="https://maps.app.goo.gl/qixtEbsHeB6ssbyP7"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-3 right-3 bg-card/95 backdrop-blur-sm text-primary text-sm font-medium px-4 py-2 rounded-full shadow-md hover:bg-card transition-colors"
              >
                Apri su Google Maps
              </a>
            </div>
          </Reveal>

          <Reveal className="bg-card p-8 rounded-3xl shadow-lg border border-border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Telefono *</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  placeholder="+39 123 456 7890"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="treatment">Trattamento di interesse</Label>
                <Select
                  value={formData.treatment}
                  onValueChange={(value) => setFormData({ ...formData, treatment: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Seleziona un trattamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {treatments.map((t) => (
                      <SelectItem key={t.slug} value={t.name}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="message">Messaggio *</Label>
                <Textarea
                  id="message"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  className="mt-2"
                />
              </div>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(v === true)}
                  className="mt-1"
                />
                <Label htmlFor="consent" className="text-sm font-normal text-muted-foreground leading-snug">
                  Ho letto e accetto la{" "}
                  <Link to="/privacy" className="text-primary underline hover:text-accent">
                    Privacy Policy
                  </Link>{" "}
                  e acconsento a essere ricontattato. *
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground rounded-full"
                size="lg"
              >
                Invia Richiesta
              </Button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
